import React, { useState } from 'react';
import { Building2, Briefcase, Users, Rocket, Wrench, UserCircle } from 'lucide-react';
import { BusinessType, BusinessSubType, QuestionnaireData } from '../../types';
import { useEffect, useRef } from 'react';

interface BusinessTypeStepProps {
  data: Partial<QuestionnaireData>;
  onChange: (data: Partial<QuestionnaireData>) => void;
}

const businessTypes = [
  {
    id: 'solopreneur' as BusinessType,
    title: 'Solopreneur',
    description: 'Running a one-person business',
    icon: UserCircle,
    subTypes: ['online-business', 'coaching', 'consulting', 'content-creator', 'service-business'] as BusinessSubType[],
  },
  {
    id: 'startup' as BusinessType,
    title: 'Startup Founder',
    description: 'Building a scalable business',
    icon: Rocket,
    subTypes: ['saas', 'marketplace', 'mobile-app', 'hardware', 'agency'] as BusinessSubType[],
  },
  {
    id: 'small-business' as BusinessType,
    title: 'Small Business Owner',
    description: 'Running an established business',
    icon: Building2,
    subTypes: ['e-commerce', 'service-business', 'consulting-firm'] as BusinessSubType[],
  },
  {
    id: 'trades' as BusinessType,
    title: 'Trades Professional',
    description: 'Providing skilled trade services',
    icon: Wrench,
    subTypes: ['plumbing', 'electrical', 'carpentry', 'hvac', 'landscaping', 'cleaning'] as BusinessSubType[],
  },
  {
    id: 'freelancer' as BusinessType,
    title: 'Freelancer',
    description: 'Working independently',
    icon: Briefcase,
    subTypes: ['content-creator', 'service-business', 'consulting-firm'] as BusinessSubType[],
  },
  {
    id: 'side-hustler' as BusinessType,
    title: 'Side Hustler',
    description: 'Growing a side project',
    icon: Users,
    subTypes: ['content-creator', 'e-commerce', 'service-business'] as BusinessSubType[],
  },
];

const subTypeLabels: Record<BusinessSubType, string> = {
  'saas': 'Software as a Service',
  'e-commerce': 'E-commerce Store',
  'marketplace': 'Online Marketplace',
  'service-business': 'Service Business',
  'content-creator': 'Content Creator',
  'agency': 'Agency',
  'consulting-firm': 'Consulting Firm',
  'mobile-app': 'Mobile App',
  'desktop-software': 'Desktop Software',
  'hardware': 'Hardware/Physical Product',
  'plumbing': 'Plumbing Services',
  'electrical': 'Electrical Services',
  'carpentry': 'Carpentry Services',
  'hvac': 'HVAC Services',
  'landscaping': 'Landscaping Services',
  'cleaning': 'Cleaning Services',
  'real-estate': 'Real Estate',
  'coaching': 'Coaching Business',
  'consulting': 'Consulting Practice',
  'online-business': 'Online Business',
  'property-management': 'Property Management',
  'retail': 'Retail Store',
  'trades': 'Trades Business',
  'marketing': 'Marketing Business',
  'startup': 'Startup',
  'other': 'Other',
};

// Named export
export function BusinessTypeStep({ data, onChange }: BusinessTypeStepProps) {
  const [selectedType, setSelectedType] = useState<BusinessType | null>(data.businessType || null);
  const [selectedSubType, setSelectedSubType] = useState<BusinessSubType | null>(data.businessSubType || null);
  const subTypeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedType && subTypeRef.current) {
      // Add a small delay to ensure the DOM has updated
      setTimeout(() => {
        subTypeRef.current?.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [selectedType]);

  const handleTypeSelect = (type: BusinessType) => {
    setSelectedType(type);
    setSelectedSubType(null);
    onChange({ 
      businessType: type, 
      businessSubType: undefined
    });
  };

  const handleSubTypeSelect = (subType: BusinessSubType) => {
    setSelectedSubType(subType);
    onChange({ businessSubType: subType });
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {businessTypes.map((type) => {
          const Icon = type.icon;
          return (
            <button
              key={type.id}
              onClick={() => handleTypeSelect(type.id)}
              className={`p-4 sm:p-6 rounded-xl border-2 transition-all text-left ${
                selectedType === type.id
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
              }`}
            >
              <Icon className={`w-6 h-6 sm:w-8 sm:h-8 mb-3 sm:mb-4 ${
                selectedType === type.id ? 'text-blue-600' : 'text-gray-600'
              }`} />
              <h3 className="text-base sm:text-lg font-semibold mb-1 sm:mb-2">{type.title}</h3>
              <p className="text-sm sm:text-base text-gray-600">{type.description}</p>
            </button>
          );
        })}
      </div>

      {selectedType && (
        <div ref={subTypeRef} className="mt-6 sm:mt-8 scroll-mt-4">
          <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">What type of {selectedType.replace('-', ' ')} are you?</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
            {businessTypes
              .find(type => type.id === selectedType)
              ?.subTypes.map(subType => (
                <button
                  key={subType}
                  onClick={() => handleSubTypeSelect(subType)}
                  className={`p-3 sm:p-4 rounded-lg border-2 transition-all text-sm sm:text-base ${
                    selectedSubType === subType
                      ? 'border-blue-600 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
                  }`}
                >
                  {subTypeLabels[subType]}
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Default export
export default BusinessTypeStep;